import Vue from "vue";
import { reactive, toRefs, computed, provide, inject } from "@vue/composition-api";
import { actions as appActions } from "./app";
import axios from "axios";
import { removeFromList, syncItemToList } from "../../plugins/formatting";

export const state = reactive({
    map: new Map(),
    allNotes: {
        client: {},
        project: {},
        supplier: {}
    },
    pinnedNotes: {
        client: {},
        project: {},
        supplier: {}
    }
});

const uploadAttachments = (note_id, uploaded_files) => {
    return new Promise(((resolve, reject) => {
        if (uploaded_files.length === 0) {
            resolve([]);
        }

        let uploaded_attachments = [];
        for (let uploaded_file of uploaded_files) {
            appActions.uploadFile({ related_type: "note", note_id, file: uploaded_file })
                .then(resp => {
                    uploaded_attachments.push(resp);
                    if (uploaded_attachments.length === uploaded_files.length) {
                        resolve(uploaded_attachments);
                    }
                })
                .catch(ex => reject(ex));
        }
    }));
};
const deleteAttachments = (deleted_files) => {
    return new Promise(((resolve, reject) => {
        if (deleted_files.length === 0) {
            resolve();
        }

        let count = deleted_files.length;
        for (let deleted_file of deleted_files) {
            axios
                .delete("note_attachment/{0}".format(deleted_file.id))
                .then(() => {
                    count--;
                    if (count === 0) {
                        resolve();
                    }
                })
                .catch(ex => reject(ex));
        }
    }));
};
const syncToPinned = note => {
    const noteFromAllNotes = state.allNotes[note.related_type][note.related_id].find(n => n.id === note.id)
    if (noteFromAllNotes) {
        noteFromAllNotes.is_pinned = note.is_pinned
    } else {
        let filteredNotes = state.allNotes[note.related_type][note.related_id]
        filteredNotes.push(note)
        filteredNotes.sort((a, b) => {
            return new Date(a.created_at) - new Date(b.created_at);
        })
        state.allNotes[note.related_type][note.related_id] = filteredNotes
    }

    let tmpPinnedNotes = Vue.clone_object(state.pinnedNotes[note.related_type][note.related_id]);
    if (note.is_pinned === 1) {
        syncItemToList(tmpPinnedNotes, note);
    } else {
        removeFromList(tmpPinnedNotes, note.id);
    }

    Vue.set(state.pinnedNotes[note.related_type], note.related_id, tmpPinnedNotes);
};

export const actions = {
    fetchNotes(related_type, related_id) {
        return new Promise((resolve, reject) => {
            axios
                .get("notes/{0}/{1}".format(related_type, related_id))
                .then(resp => {
                    Vue.set(state.pinnedNotes[related_type], related_id, resp.data.filter(x => x.is_pinned === 1));
                    Vue.set(state.allNotes[related_type], related_id, resp.data);
                    resolve(resp.data);
                })
                .catch(ex => reject(ex));
        });
    },
    saveNote(data) {
        return new Promise(((resolve, reject) => {
            uploadAttachments(data.id, data.uploaded_files)
                .then(uploaded_attachments => {
                    for (let uploaded_attachment of uploaded_attachments) {
                        data.files.push(uploaded_attachment);
                    }

                    data.uploaded_files = []

                    deleteAttachments(data.deleted_files)
                        .then(() => {
                            let url = data.id ? "notes/{0}".format(data.id) : "notes/";
                            axios
                                .post(url, data)
                                .then(resp => {
                                    syncToPinned(resp.data);
                                    resolve(resp.data);
                                })
                                .catch(ex => reject(ex));
                        })
                        .catch(ex => reject(ex));

                })
                .catch(ex => reject(ex));

        }));
    },
    pinNote(note_id) {
        return new Promise((resolve, reject) => {
            axios
                .patch("notes/{0}".format(note_id))
                .then(resp => {
                    syncToPinned(resp.data);
                    resolve(resp.data);
                })
                .catch(ex => reject(ex));
        });
    },
    deleteNote(note_id) {
        return new Promise((resolve, reject) => {
            axios
                .delete("notes/{0}".format(note_id))
                .then(() => resolve())
                .catch(ex => reject(ex));
        });
    },
};

export const getters = {
    getPinnedNotes(relatedId, relatedType, noteType) {
        let pinnedNotes = state.pinnedNotes[relatedType][relatedId] || []
        if (pinnedNotes && noteType && noteType !== "all") {
            pinnedNotes = pinnedNotes.filter(note => note.type === noteType)
        }
        return pinnedNotes
    },
    getAllNotes(relatedId, relatedType, noteType) {
        let notes = state.allNotes[relatedType][relatedId] || []
        if (noteType) {
            notes = notes.filter(note => {
                return note.type === noteType
            })
        }
        return notes
    },
    isNotePinned(note_id, relatedId, relatedType) {
        return state.pinnedNotes[relatedType][relatedId].find(note => note.id === note_id)
    }
};

export const key = Symbol("NoteModule");

export const provideNote = () =>
    provide(key, {
        ...toRefs(state),
        ...getters,
        ...actions
    });

export default () => inject(key);
